import React, { useState, useEffect, useRef } from "react";
import { Button, TextField, Typography } from "@mui/material";
import SignatureCanvas from "react-signature-canvas";
import { useLocation } from "react-router-dom";
import { styled } from "@mui/system";
import QuoteHeader from "../QuoteForm/QuoteHeader";
import generatePDF from "../../../tools/generateQuoteSummaryPDF";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import { decompressFromEncodedURIComponent } from "lz-string";
import InvoiceDetails from "./InvoiceSummaryDetailsSpanish";
import RowsTable from "./RowsSummaryTableSpanish";
import CompanyInfo from "../CompanyPymtInfoSpanish";
import SpecialNotes from "../SpecialNotesSpanish";
import NotesSummary from "./NotesSummarySpanish";
import ThankYouForB from "../ThankyouSpanish";

export default function SpanishQuoteSummary() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const orderNumberFromURL = decodeURIComponent(queryParams.get("orderNumber"));
  const compressedDataFromURL = queryParams.get("data");
  const invoiceData = JSON.parse(
    decompressFromEncodedURIComponent(compressedDataFromURL)
  );

  const [total, setTotal] = useState(0);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [printedName, setPrintedName] = useState("");

  const sigCanvas = useRef({});
  const clearSignature = () => {
    if (sigCanvas && sigCanvas.current) {
      sigCanvas.current.clear();
    }
  };

  useEffect(() => {
    if (invoiceData && Array.isArray(invoiceData.rows)) {
      const newTotal = invoiceData.rows.reduce(
        (acc, row) => acc + (Number(row.price) || 0),
        0
      );
      setTotal(newTotal);
    }
  }, [invoiceData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const signatureDataUrl = sigCanvas.current.toDataURL();

    const invoice = {
      name: invoiceData.name,
      companyName: invoiceData.companyName,
      email: invoiceData.email,
      truck: invoiceData.truck,
      origin: invoiceData.origin,
      destination: invoiceData.destination,
      transitTime: invoiceData.transitTime,
      address: invoiceData.address,
      distance: invoiceData.distance,
      notes: invoiceData.notes,
      rows: invoiceData.rows,
      signature: signatureDataUrl,
      printedName,
    };

    generatePDF(invoice, orderNumberFromURL);
    setOpenSnackbar(true);

    // Also, clear the signature after submitting the form
    clearSignature();
    setPrintedName("");
  };

  const StyledButton = styled(Button)({
    fontWeight: "bold",
    backgroundColor: "#000",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#ff0000",
    },
  });

  return (
    <>
      <QuoteHeader orderNumber={orderNumberFromURL} />
      <form
        onSubmit={handleSubmit}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "16px",
          margin: "0 auto",
        }}
      >
        {/*Invoice details table*/}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            fontFamily: "Lato, sans-serif",
          }}
        >
          <InvoiceDetails invoiceData={invoiceData} />
        </div>

        {/*Description and price table*/}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            fontFamily: "Lato, sans-serif",
            flexDirection: "column",
          }}
        >
          <RowsTable rows={invoiceData.rows} total={total} />
        </div>

        {/*Notes table */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            fontFamily: "Lato, sans-serif",
          }}
        >
          <NotesSummary invoiceData={invoiceData} />
        </div>

        {/*Special Notes table */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            fontFamily: "Lato, sans-serif",
          }}
        >
          <SpecialNotes />
        </div>

        {/*Pymt Info table */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            fontFamily: "Lato, sans-serif",
          }}
        >
          <CompanyInfo />
        </div>

        <TextField
          variant="outlined"
          margin="normal"
          required
          fullWidth
          id="printedName"
          label="Nombre impreso"
          name="printedName"
          autoFocus
          value={printedName}
          onChange={(e) => setPrintedName(e.target.value)}
        />

        {/*Signature field - start */}
        <Typography
          variant="h6"
          style={{ marginTop: "20px", fontFamily: "Lato, sans-serif" }}
        >
          Firma
        </Typography>
        <SignatureCanvas
          ref={sigCanvas}
          canvasProps={{
            width: 500,
            height: 200,
            className: "signature-canvas",
          }}
          penColor="black"
        />
        <Button
          onClick={clearSignature}
          variant="outlined"
          color="error"
          style={{ marginTop: "10px", marginRight: "10px" }}
        >
          Clear Signature
        </Button>
        {/*Signature field - end */}

        <ThankYouForB />

        <StyledButton
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          style={{ margin: "20px 0" }}
        >
          Estoy de acuerdo
        </StyledButton>
      </form>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={1500}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity="success">
          Información enviada correctamente, puede cerrar la ventana.
        </Alert>
      </Snackbar>
    </>
  );
}
